import React, {useEffect, useRef, useState} from 'react';
import {IonContent, IonIcon, IonPage, useIonAlert} from '@ionic/react';
import { useSwipeable } from 'react-swipeable';
import { AnimatePresence, motion } from 'framer-motion';
import {
    bookmark,
    bookmarkOutline,
    downloadOutline, newspaperOutline,
    shareSocialOutline
} from "ionicons/icons";
import NewsDetailSheetModal from "../components/NewsDetailSheetModal";
import useStore from "../zustand/useStore";
import {FacebookLoader} from "../components/Loader/FacebookLoader";
import {
    actionToGetCurrentLatestNewsCategories, actionToGetNewsById,
    actionToGetNewsDataByCategoryFilter,
    actionToGetNewsDataByCategoryFilterOffset, actionToMarkContentRead, actionToMarkUnmarkBookmarkNewsData
} from "../apiHelpers/CommonAction";
import moment from "moment-timezone";
import { Share } from '@capacitor/share';
import {Capacitor} from "@capacitor/core";
import {hideBanner, resumeBanner} from "../lib/ads";
import {useParams} from "react-router";

export default function HomeMobilePage({renderHeaderPage}) {
    const [presentAlert] = useIonAlert();
    const {isDetailOpen, setIsDetailOpen} = useStore();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [categoryIndex, setCategoryIndex] = useState(0);
    const [direction, setDirection] = useState('up');
    const [isBookMarkFilterActive, setIsBookMarkFilterActive] = useState(false);
    const newsContentRef = useRef(null);
    const newsContentSectionRef = useRef(null);
    const {selectedNewsCategory,newsDetailsData,setNewsDetailsData,setSelectedNewsCategory,requestNewsCategory,setNewsCategoryData,newsDetailDataList,newsCategory} = useStore();
    const {loading,newsDetailList} = newsDetailDataList;
    const news = newsDetailList[currentIndex];
    const markingRef = useRef(null);     // last marked news id
    const loadingMoreRef = useRef(false); // prevent s
    const {id} = useParams();

    useEffect(()=>{
        if(id) {
            actionToGetNewsById(id).then((newsData)=>{
                if(newsData?.id) {
                    setNewsDetailsData(newsData);
                    setIsDetailOpen(true);
                }
            });
            setTimeout(()=>{
                window.history.pushState({},null,`${window.location.origin}/dashboard/home`);
            },1000)
        }
    },[id])

    const markCurrentAsRead = (id) => {
        if (!id || markingRef.current === id) return;
        markingRef.current = id;
        // fire-and-forget; backend uses session cookie
        actionToMarkContentRead('news', id);
    };

    const maybeLoadMore = () => {
        // when 3 from the end, fetch next page
        if (loadingMoreRef.current) return;
        if (currentIndex >= (newsDetailList.length - 3)) {
            loadingMoreRef.current = true;
            actionToGetNewsDataByCategoryFilterOffset(selectedNewsCategory,isBookMarkFilterActive);
            // release lock after a tick (store will update list)
            setTimeout(() => { loadingMoreRef.current = false; }, 400);
        }
    };

    const callFunctionToGetCategoryData = (category,index)=>{
        setCategoryIndex(index);
        setCurrentIndex(0);
        markingRef.current = null;
        loadingMoreRef.current = false;
        setSelectedNewsCategory(category);
        actionToGetNewsDataByCategoryFilter(category);
        hideBanner();
        setTimeout(()=>{
            resumeBanner();
        },500)
        setIsBookMarkFilterActive(false);
    }

    useEffect(()=>{
        callFunctionToGetCategoryData('All',0);
    },[])

    const handleBookMarkFilter = () => {
        setIsBookMarkFilterActive(!isBookMarkFilterActive);
        setCurrentIndex(0);
        setCategoryIndex(0);
        actionToGetNewsDataByCategoryFilter('All',true);
    }

    const handlers = useSwipeable({
        onSwipedUp: () => {
            if (currentIndex < newsDetailList.length - 1) {
                // mark the one we’re leaving as read
                const currentCard = newsDetailList[currentIndex];
                markCurrentAsRead(currentCard?.id);
                setDirection('up');
                setCurrentIndex((i) => i + 1);
                // prefetch next page when close to tail
                maybeLoadMore();
                hideBanner();
                setTimeout(()=>{
                    resumeBanner();
                },500)
            }
        },
        onSwipedDown: () => {
            if (currentIndex > 0) {
                setDirection('down');
                setCurrentIndex(currentIndex - 1);
                hideBanner();
                setTimeout(()=>{
                    resumeBanner();
                },500)
            }
        },
        onSwipedLeft: () => {
            if (categoryIndex < newsCategory?.categoryData?.length - 1) {
                setCategoryIndex(categoryIndex + 1);
                setCurrentIndex(0);
                callFunctionToGetCategoryData(newsCategory?.categoryData[categoryIndex + 1]?.category,categoryIndex + 1);
                hideBanner();
                setTimeout(()=>{
                    resumeBanner();
                },500)
            }
        },
        onSwipedRight: () => {
            if (categoryIndex > 0) {
                setCategoryIndex(categoryIndex - 1);
                setCurrentIndex(0);
                callFunctionToGetCategoryData(newsCategory?.categoryData[categoryIndex - 1]?.category,categoryIndex - 1);
                hideBanner();
                setTimeout(()=>{
                    resumeBanner();
                },500)
            }
        },
        trackMouse: true,
    });

    const openDetailPanel = (newsData) => {
        setNewsDetailsData(newsData);
        setIsDetailOpen(true);
    }

    const handleRefresh = (event) => {
        callFunctionToGetCategoryData(selectedNewsCategory,categoryIndex);
        setTimeout(() => {
            event.detail.complete();
        }, 2000);
    };


    const callFunctionToDownloadPdf = (urlPath) => {
        if(!urlPath){
            presentAlert({
                header: 'Error Downloading!',
                message: 'No downloadable content found.',
                buttons: [{
                    text: 'Ok',
                }],
            });
            return;
        }
        const link = document.createElement('a');
        link.href = urlPath;

        // Extract file name from URL or set a default
        const fileName = urlPath.split('/').pop() || 'downloaded-file.pdf';
        link.download = fileName;

        // Trigger the download
        document.body.appendChild(link);
        link.click();

        // Clean up
        document.body.removeChild(link);
    };

    const handleShareUrl = async (news) => {
        try {
            const url = `https://caworld.org/api-ca-world/common/shareAppContentData/news/${news?.id}`;
            const title = 'CA World News';
            const text = 'Check out this news!';


            // Prefer native Share plugin when running in a Capacitor app.
            // Don't gate on canShare() for text/URL — it's mainly helpful for FILE sharing.
            const isNative = Capacitor.getPlatform() !== 'web';
            const hasPlugin = Capacitor.isPluginAvailable?.('Share');

            if (isNative && hasPlugin) {
                await Share.share({
                    title,
                    text,
                    url,                  // supports http/https and file://
                    dialogTitle: 'Share this news', // Android only
                });
                return;
            }

            // Web Share API (must be triggered from a direct user gesture)
            if (navigator?.share) {
                await navigator.share({ title, text, url });
                return;
            }

            // Fallbacks
            try {
                await navigator?.clipboard?.writeText?.(url);
                presentAlert({
                    header: 'Error Sharing!',
                    message: 'Link copied to clipboard!',
                    buttons: [{
                        text: 'Ok',
                    }],
                });
            } catch {
                window.prompt('Sharing is not supported. Copy this link:', url);
            }
        } catch (err) {
            console.error('Sharing failed:', err);
            presentAlert({
                header: 'Error Sharing!',
                message: 'Failed to share!',
                buttons: [{
                    text: 'Ok',
                }],
            });
        }
    };

    useEffect(()=>{
        actionToGetCurrentLatestNewsCategories(requestNewsCategory,setNewsCategoryData);
    },[])

    const panelSubHeader = ()=>{
        return (
            <div className="news-menu-container">
                <div className="news-menu-header">
                    {(newsCategory?.loading) ?
                        <FacebookLoader type={"facebookStyle"} item={1}/>
                        :
                        <div className="news-menu">
                            <button
                                onClick={() => handleBookMarkFilter()}
                                className={`news-tab ${isBookMarkFilterActive ? 'active' : ''}`}>
                                <IonIcon icon={isBookMarkFilterActive ? bookmark : bookmarkOutline} slot="start"/>
                            </button>
                            {Array.isArray(newsCategory?.categoryData)
                                ? newsCategory.categoryData.map(({ category }, index) => (
                                <button
                                    key={category}
                                    onClick={() => callFunctionToGetCategoryData(category,index)}
                                    className={`news-tab ${!isBookMarkFilterActive && category === selectedNewsCategory ? 'active' : ''}`}>
                                    {category && category?.replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                                </button>
                                ))
                                : null}
                        </div>
                    }
                </div>
            </div>
        )
    }

    const callFunctionToBookmarkNews = async (news_id,is_bookmarked)=>{
        await actionToMarkUnmarkBookmarkNewsData({news_id},is_bookmarked)
    }

    return (
        <IonPage>
            {/*/////// RENDER HEADER PART ////////*/}
            {renderHeaderPage && renderHeaderPage(panelSubHeader)}
            {/*/////// RENDER HEADER PART ////////*/}

            <IonContent fullscreen className="page-content">
                <div className="news-feed-page">
                    {(loading) ?
                        <div className="news-card-stack">
                            <FacebookLoader type={"facebookStyle"} item={1}/>
                            <FacebookLoader type={"facebookStyle"} item={1}/>
                            <FacebookLoader type={"facebookStyle"} item={1}/>
                        </div>
                        :
                        <div className="news-card-stack" {...handlers}>
                            {(news?.id) ?
                                <AnimatePresence mode="popLayout">
                                    <motion.div
                                        key={news?.id}
                                        className="news-card"
                                        initial={{
                                            y: direction === 'up' ? 300 : -300
                                        }}
                                        animate={{
                                            y: 0
                                        }}
                                        transition={{duration: 0.25}}>
                                        <div className={"news-image-content "+Capacitor.getPlatform()}>
                                            <img className="news-image" src={news?.image_url} alt={news?.title}/>
                                        </div>
                                        {(Capacitor.getPlatform() !== 'ios') ?
                                        <div id={'web-ad-banner-center'} className={"add_in_main_news_page_appear_here"}>
                                            <div>
                                                Google ads will appear here
                                            </div>
                                        </div>:''
                                        }
                                        <div className="news-content" data-publisher-content="true">
                                            <div className="news-sub-h-text">
                                                <div className="news-category">
                                                    {news.category.charAt(0).toUpperCase() + news.category.slice(1)}
                                                </div>
                                                <div className="news-actions">
                                                    <IonIcon icon={downloadOutline} onClick={()=>callFunctionToDownloadPdf(news?.attachment_url)}></IonIcon>
                                                    <IonIcon className={`${news?.is_bookmarked ? 'bookmarked_icon_color' : ''}`} onClick={()=>callFunctionToBookmarkNews(news?.id,news?.is_bookmarked)}
                                                             icon={news?.is_bookmarked ? bookmark : bookmarkOutline}>
                                                    </IonIcon>
                                                    <IonIcon icon={shareSocialOutline} onClick={()=>handleShareUrl(news)}></IonIcon>
                                                </div>
                                            </div>
                                            <hr/>
                                            <div ref={newsContentSectionRef} className={"news_detail_card_section_text_info"}>
                                                <div className="news-detail-source">

                                                        <a className={"source_link_section"} href={news?.source_url}>
                                                            <span className={"source_just_tap_tag"}> Source: </span><strong>{news?.source}</strong>
                                                        </a>

                                                    <span>• {moment(news?.created_at).fromNow()}</span>
                                                    <div className="read_more_in_news_button" onClick={() => openDetailPanel(news)}>
                                                        Read More
                                                    </div>
                                                </div>
                                                <h2 onClick={() => openDetailPanel(news)}>{news?.title}</h2>
                                                <div ref={newsContentRef} className={"news_detail_card_p_content"} dangerouslySetInnerHTML={{__html:news?.description}}></div>
                                            </div>
                                        </div>
                                    </motion.div>
                                </AnimatePresence>
                                :
                                <div className={"no_news_data_found_div"}>
                                    <IonIcon icon={newspaperOutline}/>
                                    <p>No News Data Found</p>
                                </div>
                            }
                        </div>
                    }
                </div>
            </IonContent>
            {(isDetailOpen) ?
            <NewsDetailSheetModal isOpen={isDetailOpen}
                                  newsDetailsData={newsDetailsData}
                                  onDidDismiss={() => setIsDetailOpen(false)}/>:''
            }
        </IonPage>
    );
}

